import { useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Badge } from '@capawesome/capacitor-badge';

/**
 * Syncs the native app icon badge count with the number of unread notifications.
 * On web / unsupported platforms this is a no-op.
 */
export function useAppBadge(unreadCount: number) {
  const isNative = Capacitor.isNativePlatform();

  const setBadge = useCallback(async (count: number) => {
    if (!isNative) return;
    try {
      // Request permission if needed (iOS only)
      const { display } = await Badge.checkPermissions();
      if (display === 'prompt' || display === 'prompt-with-rationale') {
        await Badge.requestPermissions();
      }
      if (count > 0) {
        await Badge.set({ count });
      } else {
        await Badge.clear();
      }
    } catch (err) {
      // Badge API is best-effort; swallow errors silently
      console.warn('Badge update failed:', err);
    }
  }, [isNative]);

  const clearBadge = useCallback(async () => {
    if (!isNative) return;
    try {
      await Badge.clear();
    } catch (err) {
      console.warn('Badge clear failed:', err);
    }
  }, [isNative]);

  // Keep badge in sync whenever unread count changes
  useEffect(() => {
    setBadge(unreadCount);
  }, [unreadCount, setBadge]);

  // Clear badge when user brings the app to the foreground
  useEffect(() => {
    if (!isNative) return;
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) clearBadge();
    });
    return () => { listener.then(l => l.remove()); };
  }, [isNative, clearBadge]);

  return { clearBadge };
}
